import { createSlice } from "@reduxjs/toolkit";
import {
  createCertificateEntity,
  getActiveCertificateEntities,
  getAllCertificateEntities,
  updateCertificateEntity,
} from "./certificateEntityService";

/* -------------------- Status Enum -------------------- */

export const STATUS = {
  IDLE: "idle",
  LOADING: "loading",
  SUCCEEDED: "succeeded",
  FAILED: "failed",
};

/* -------------------- Initial State -------------------- */

const initialState = {
  createCertificateEntity: {
    status: STATUS.IDLE,
    error: null,
    message: null,
  },

  certificateEntityList: {
    status: STATUS.IDLE,
    error: null,
    data: [],
    pageCount: 0,
  },

  updateCertificateEntity: {
    status: STATUS.IDLE,
    error: null,
    message: null,
  },
  
  activeCertificateEntityList: {
    status: STATUS.IDLE,
    error: null,
    data: [],
  },
};

/* -------------------- Slice -------------------- */

const certificateEntitySlice = createSlice({
  name: "certificateEntity",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    /* ---------- Create Certificate Entity ---------- */
    builder
      .addCase(createCertificateEntity.pending, (state) => {
        state.createCertificateEntity.status = STATUS.LOADING;
        state.createCertificateEntity.error = null;
      })
      .addCase(createCertificateEntity.fulfilled, (state, action) => {
        state.createCertificateEntity.status = STATUS.SUCCEEDED;
        state.createCertificateEntity.message = action?.payload;
      })
      .addCase(createCertificateEntity.rejected, (state, action) => {
        state.createCertificateEntity.status = STATUS.FAILED;
        state.createCertificateEntity.error = action?.payload;
      });

    /* ---------- Get Certificate Entity List ---------- */
    builder
      .addCase(getAllCertificateEntities.pending, (state) => {
        state.certificateEntityList.status = STATUS.LOADING;
        state.certificateEntityList.error = null;
      })
      .addCase(getAllCertificateEntities.fulfilled, (state, action) => {
        state.certificateEntityList.status = STATUS.SUCCEEDED;
        state.certificateEntityList.data = action?.payload || [];
        state.certificateEntityList.pageCount = action.payload?.pageCount || 0;
      })
      .addCase(getAllCertificateEntities.rejected, (state, action) => {
        state.certificateEntityList.status = STATUS.FAILED;
        state.certificateEntityList.error = action.payload?.message;
      });

          /* ---------- Update Certificate Entity ---------- */
    builder
      .addCase(updateCertificateEntity.pending, (state) => {
        state.updateCertificateEntity.status = STATUS.LOADING;
        state.updateCertificateEntity.error = null;
      })
      .addCase(updateCertificateEntity.fulfilled, (state, action) => {
        state.updateCertificateEntity.status = STATUS.SUCCEEDED;
        state.updateCertificateEntity.message = action?.payload;
      })
      .addCase(updateCertificateEntity.rejected, (state, action) => {
        state.updateCertificateEntity.status = STATUS.FAILED;
        state.updateCertificateEntity.error = action?.payload;
      });

    /* ---------- Get Active Certificate Entity List ---------- */
    builder
      .addCase(getActiveCertificateEntities.pending, (state) => {
        state.activeCertificateEntityList.status = STATUS.LOADING;
        state.activeCertificateEntityList.error = null;
      })
      .addCase(getActiveCertificateEntities.fulfilled, (state, action) => {
        state.activeCertificateEntityList.status = STATUS.SUCCEEDED;
        state.activeCertificateEntityList.data = action?.payload || [];
      })
      .addCase(getActiveCertificateEntities.rejected, (state, action) => {
        state.activeCertificateEntityList.status = STATUS.FAILED;
        state.activeCertificateEntityList.error = action.payload?.message;
      });

  },
});

/* -------------------- Selectors -------------------- */

export const selectCreateCertificate = (state) =>
  state.certificateEntity.createCertificateEntity;

export const selectCertificateList = (state) =>
  state.certificateEntity.certificateEntityList;

export const selectUpdateCertificateEntity = (state) =>
  state.certificateEntity.updateCertificateEntity;

export const selectActiveCertificateList = (state) =>
  state.certificateEntity.activeCertificateEntityList;

export default certificateEntitySlice.reducer;
